/**
 * Product API Service
 * Secure and optimized API calls for product-related operations
 */

// import {httpClient as apiClient} from '@http';
import  { apiClient } from '@/auth';
import type { Product, ProductDetails, ProductFilters } from '../types';
import type { ApiResponse } from '@http';

// Product API query parameters interface
interface ProductQueryParams extends ProductFilters {
    limit?: number;
    offset?: number;
    [key: string]: unknown;
}

// Product API service class
export class ProductApiService {
    /**
     * Get products list with filtering and pagination
     * @param params - Query parameters for filtering and pagination
     * @returns Promise<{ products: Product[], total: number }> - Products list and total count
     */
    static async getProducts(params: ProductQueryParams = {}): Promise<{ products: Product[], total: number }> {
        try {
            const response: ApiResponse<Product[]> = await apiClient.get<Product[]>('products/', params);

            // Extract total count from headers (common pattern for pagination)
            const total = parseInt(response.headers.get('X-Total-Count') || '0', 10);

            return {
                products: response.data,
                total,
            };
        } catch (error) {
            console.error('Failed to fetch products:', error);
            throw error;
        }
    }

    /**
     * Get product details by ID
     * @param productId - Product ID
     * @returns Promise<ProductDetails> - Detailed product information
     */
    static async getProductById(productId: string | number): Promise<ProductDetails> {
        try {
            const response: ApiResponse<ProductDetails> = await apiClient.get<ProductDetails>(`products/${productId}`);
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch product ${productId}:`, error);
            throw error;
        }
    }

    /**
     * Create a new product
     * @param productData - Product data to create
     * @returns Promise<ProductDetails> - Created product details
     */
    static async createProduct(productData: Partial<Omit<ProductDetails, 'id' | 'user_id' | 'created_at' | 'updated_at'>>): Promise<ProductDetails> {
        try {
            const response: ApiResponse<ProductDetails> = await apiClient.post<ProductDetails>('products/', productData);
            return response.data;
        } catch (error) {
            console.error('Failed to create product:', error);
            throw error;
        }
    }

    /**
     * Update an existing product
     * @param productId - Product ID
     * @param productData - Updated product data
     * @returns Promise<ProductDetails> - Updated product details
     */
    static async updateProduct(
        productId: string | number,
        productData: Partial<Omit<ProductDetails, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
    ): Promise<ProductDetails> {
        try {
            const response: ApiResponse<ProductDetails> = await apiClient.patch<ProductDetails>(`products/${productId}`, productData);
            return response.data;
        } catch (error) {
            console.error(`Failed to update product ${productId}:`, error);
            throw error;
        }
    }

    /**
     * Delete a product
     * @param productId - Product ID
     * @returns Promise<void>
     */
    static async deleteProduct(productId: number): Promise<void> {
        try {
            await apiClient.delete(`products/${productId}`);
        } catch (error) {
            console.error(`Failed to delete product ${productId}:`, error);
            throw error;
        }
    }

    /**
     * Upload multiple images for a product (bulk upload)
     * @param productId - Product ID
     * @param imageFiles - Array of image files to upload (maximum 5)
     * @returns Promise<ProductDetails> - Updated product with new images
     */
    static async uploadProductImages(productId: number | string, imageFiles: File[]): Promise<ProductDetails> {
        try {
            if (imageFiles.length > 5) {
                throw new Error('Maximum 5 images allowed per product');
            }

            const formData = new FormData();
            Array.from(imageFiles).forEach((file) => {
                formData.append('files', file);
            });

            const response: ApiResponse<ProductDetails> = await apiClient.post<ProductDetails>(
                `products/${productId}/images/bulk`,
                formData,
            );
            return response.data;
        } catch (error) {
            console.error(`Failed to upload images for product ${productId}:`, error);
            throw error;
        }
    }

    /**
     * Upload multiple images for a product (bulk upload)
     * @param productId - Product ID
     * @param imageId - Image ID to delete
     * @returns Promise<ProductDetails> - Updated product with new images
     */
    static async deleteProductImage(productId: number | string, imageId: number | string): Promise<ProductDetails> {
        try {
            const response: ApiResponse<ProductDetails> = await apiClient.delete<ProductDetails>(
                `products/${productId}/images/${imageId}`,
            );
            return response.data;
        } catch (error) {
            console.error(`Failed to upload images for product ${productId}:`, error);
            throw error;
        }
    }


    /**
     * Update a product's status
     * @param productId - Product ID
     * @param status - New product status ('Active', 'Inactive', or 'Archived')
     * @returns Promise<ProductDetails> - Updated product details
     */
    static async updateProductStatus(productId: string | number, status: 'Active' | 'Inactive' | 'Archived'): Promise<ProductDetails> {
        try {
            const response: ApiResponse<ProductDetails> = await apiClient.patch<ProductDetails>(
                `products/${productId}`,
                { status }
            );
            return response.data;
        } catch (error) {
            console.error(`Failed to update product status to ${status} for product ${productId}:`, error);
            throw error;
        }
    }

    /**
     * Get outreach email template for a product
     * @param productId - Product ID
     * @returns Promise<{subject: string, body: string}> - Email template data
     */
    static async getProductOutreachEmail(productId: string | number): Promise<{subject: string, body: string}> {
        try {
            const response: ApiResponse<{subject: string, body: string}> = await apiClient.get<{subject: string, body: string}>(`products/${productId}/outreach-email`);
            return response.data;
        } catch (error) {
            console.error(`Failed to fetch outreach email for product ${productId}:`, error);
            throw error;
        }
    }

    /**
     * Update outreach email template for a product
     * @param productId - Product ID
     * @param emailData - Email template data (subject and body)
     * @returns Promise<{subject: string, body: string}> - Updated email template data
     */
    static async updateProductOutreachEmail(
        productId: string | number,
        emailData: {subject: string, body: string}
    ): Promise<{subject: string, body: string}> {
        try {
            const response: ApiResponse<{subject: string, body: string}> = await apiClient.post<{subject: string, body: string}>(`products/${productId}/outreach-email`, emailData);
            return response.data;
        } catch (error) {
            console.error(`Failed to update outreach email for product ${productId}:`, error);
            throw error;
        }
    }

    /**
     * Get suppliers list
     * @param params - Query parameters for filtering and pagination
     * @returns Promise<{ products: Product[], total: number }> - Products list and total count
     */
    static async getSuppliers(params: ProductQueryParams = {}): Promise<{ products: Product[], total: number }> {
        try {
            const response: ApiResponse<Product[]> = await apiClient.get<Product[]>('products/', params);

            // Extract total count from headers (common pattern for pagination)
            const total = parseInt(response.headers.get('X-Total-Count') || '0', 10);

            return {
                products: response.data,
                total,
            };
        } catch (error) {
            console.error('Failed to fetch products:', error);
            throw error;
        }
    }

}
